import React, { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * Deterministically generates a vibrant, modern HSL gradient based on string hash.
 * Used for workspaces that don't have a custom brand logo uploaded to Storage.
 */
function getWorkspaceGradient(seed = 'workspace') {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue1 = Math.abs(hash) % 360;
    const hue2 = (hue1 + 40) % 360;
    return `linear-gradient(135deg, hsl(${hue1}, 75%, 48%), hsl(${hue2}, 85%, 38%))`;
}

/**
 * Normalizes workspace display name, resolving system placeholders
 * ("Primary Workspace", "Primary Organization") to actual brand identities.
 */
export function normalizeWorkspaceName(workspace) {
    if (!workspace) return 'The Scale Agency';
    let name = typeof workspace === 'string' ? workspace : (workspace.name || '');
    name = name.replace(' (Fallback)', '').trim();

    const domain = (typeof workspace === 'object' ? (workspace.domain || workspace.id || '') : '').toLowerCase();
    const id = (typeof workspace === 'object' ? (workspace.id || '') : '').toLowerCase();

    // Map system default tenant to The Scale Agency
    if (!name || name === 'Primary Workspace' || name === 'Primary Organization' || name === 'Loading...') {
        if (domain.includes('the2conf') || id.includes('the2conf')) {
            return 'The 2.0 Conferences';
        }
        return 'The Scale Agency';
    }

    return name;
}

/**
 * Extracts 1-2 clean uppercase initials from a workspace name.
 * Handles "The Scale Agency" -> "SA", "The 2.0 Conferences" -> "2C", "Acme Corp" -> "AC", etc.
 */
export function getWorkspaceInitials(workspace) {
    const name = typeof workspace === 'string' ? normalizeWorkspaceName({ name: workspace }) : normalizeWorkspaceName(workspace);
    if (!name) return 'SA';

    // Strip "The " prefix
    const clean = name.replace(/^(The\s+)/i, '').trim();
    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return clean.slice(0, 2).toUpperCase() || 'SA';
}

/**
 * Resolves the optimal logo URL from Firestore, Storage, or built-in brand SVGs.
 */
export function resolveWorkspaceLogo(workspace, theme = 'dark') {
    if (!workspace) return '/Workspaces/tsa_mark.svg';

    // 1. Direct custom logo from Firestore/Storage
    const directLogo = theme === 'dark'
        ? (workspace.darkLogoUrl || workspace.lightLogoUrl || workspace.logoUrl || workspace.logo || workspace.avatarUrl || workspace.brandLogo)
        : (workspace.lightLogoUrl || workspace.darkLogoUrl || workspace.logoUrl || workspace.logo || workspace.avatarUrl || workspace.brandLogo);

    if (directLogo && typeof directLogo === 'string' && directLogo.trim().length > 0) {
        return directLogo.trim();
    }

    // 2. Built-in enterprise brand mapping
    const nameLower = (workspace.name || '').toLowerCase();
    const domainLower = (workspace.domain || '').toLowerCase();
    const idLower = (workspace.id || '').toLowerCase();

    // The Scale Agency
    if (
        nameLower.includes('scale agency') || 
        nameLower.includes('the scale') || 
        domainLower.includes('thescaleagency') || 
        idLower.includes('scale-agency') ||
        nameLower === 'primary workspace' ||
        nameLower === 'primary organization'
    ) {
        return '/Workspaces/tsa_mark.svg';
    }

    // The 2.0 Conferences
    if (
        nameLower.includes('2.0 conference') || 
        nameLower.includes('the 2.0') || 
        domainLower.includes('the2conf') || 
        idLower.includes('the2conf')
    ) {
        return '/Workspaces/the2-conf-color-logo.svg';
    }

    // Oblique / Eleventh
    if (nameLower.includes('oblique') || domainLower.includes('oblique') || nameLower.includes('eleventh')) {
        return theme === 'dark' ? '/brand/oblique-dark-icon.svg' : '/brand/oblique-light-icon.svg';
    }

    return null;
}

/**
 * Enterprise Workspace Brand Avatar
 * Handles 1000+ workspaces dynamically:
 * - Displays uploaded light/dark logo from Firebase Storage / Firestore
 * - Resolves verified enterprise brands (The Scale Agency, The 2.0 Conferences, etc.)
 * - Seamlessly falls back to an industry-standard deterministic monogram if no logo exists
 */
export default function WorkspaceAvatar({ 
    workspace, 
    size = 'sm', 
    className = '', 
    style = {},
    showBorder = true
}) {
    const { theme } = useTheme();
    const [imageError, setImageError] = useState(false);

    const displayName = normalizeWorkspaceName(workspace);
    const initials = getWorkspaceInitials(workspace);
    const gradient = getWorkspaceGradient(workspace?.id || displayName);
    const logoUrl = resolveWorkspaceLogo(workspace, theme);

    // Dimensions map
    const sizeConfig = {
        xs: { box: 'w-4 h-4 rounded', text: 'text-[9px]', imgMax: 'max-h-3.5 max-w-3.5' },
        sm: { box: 'w-5 h-5 rounded-md', text: 'text-[10px]', imgMax: 'max-h-4 max-w-4' },
        md: { box: 'w-8 h-8 rounded-lg', text: 'text-xs', imgMax: 'max-h-6 max-w-6' },
        lg: { box: 'w-12 h-12 rounded-xl', text: 'text-base', imgMax: 'max-h-9 max-w-9' },
        xl: { box: 'w-16 h-16 rounded-2xl', text: 'text-xl', imgMax: 'max-h-12 max-w-12' },
    }[size] || { box: 'w-5 h-5 rounded-md', text: 'text-[10px]', imgMax: 'max-h-4 max-w-4' };

    const hasValidImage = Boolean(logoUrl && !imageError);

    return (
        <div 
            className={`inline-flex items-center justify-center shrink-0 overflow-hidden select-none font-bold ${sizeConfig.box} ${
                showBorder ? 'border border-[var(--border)]' : ''
            } ${className}`}
            style={{
                background: hasValidImage ? 'var(--bg-subtle)' : gradient,
                color: '#ffffff',
                ...style
            }}
            title={displayName}
        >
            {hasValidImage ? (
                <img
                    src={logoUrl}
                    alt={displayName}
                    className={`w-full h-full ${sizeConfig.imgMax} object-contain p-0.5`}
                    onError={() => setImageError(true)}
                />
            ) : (
                <span className={`tracking-wider leading-none font-semibold ${sizeConfig.text}`}>
                    {initials}
                </span>
            )}
        </div>
    );
}
